<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'SharePoint Online Governance and Configuration Service — QikSolve',
  description:
    'Practical SharePoint Online governance and configuration support for controlled records, ownership, access, and evidence.',
});

const page = {
  hero: {
    eyebrow: 'SharePoint governance',
    title: 'Create a SharePoint environment that people can trust and use consistently',
    summary:
      'SharePoint Online can support controlled information management, but the real value comes from strong governance, sensible configuration, and clear ownership. QikSolve helps organisations shape a practical Microsoft 365 model that supports day-to-day work without creating unnecessary friction.',
    cta: { label: 'Discuss your governance needs', to: '/contact' },
  },
  intro: {
    title: 'Governance is what turns a platform into a usable control system',
    content:
      'A SharePoint environment often starts as a place for documents and collaboration, but without clear governance it can become difficult to navigate, maintain, and trust. The goal is not more process for its own sake — it is a practical, proportionate approach that supports accountability, records quality, and everyday operating discipline.',
    items: [
      {
        title: 'Clear accountability',
        description: 'Responsibility for content, permissions, reviews, and governance decisions needs to be visible to the people who use the system.',
      },
      {
        title: 'Configured for real work',
        description: 'Metadata, permissions, site structure, and retention settings should support operational needs rather than add unnecessary complexity.',
      },
      {
        title: 'Built for evidence and review',
        description: 'Where quality or compliance requirements matter, governance and configuration choices should support traceability and assurance.',
      },
    ],
  },
  sections: [
    {
      title: 'What this service supports',
      items: [
        {
          title: 'Governance design',
          description: 'Define ownership, access principles, review expectations, and information responsibilities in a way that teams can understand and apply.',
        },
        {
          title: 'Platform configuration',
          description: 'Shape site structures, metadata, permissions, retention patterns, and working practices to suit how the organisation actually operates.',
        },
        {
          title: 'Operational maturity',
          description: 'Build a system that is easier to sustain, review, and improve as requirements change over time.',
        },
        {
          title: 'Quality-sensitive environments',
          description: 'Support organisations that need governance and configuration decisions to align with controlled records and evidential practice.',
        },
      ],
    },
    {
      title: 'Why it matters',
      items: [
        {
          title: 'Reduces confusion',
          description: 'When ownership and responsibilities are clear, teams spend less time guessing what is expected and more time working effectively.',
        },
        {
          title: 'Improves consistency',
          description: 'Structured governance supports more consistent ways of creating, storing, approving, and reviewing information.',
        },
        {
          title: 'Supports control without burden',
          description: 'A proportionate configuration model helps teams maintain control without creating unnecessary administrative overhead.',
        },
      ],
    },
    {
      title: 'How QikSolve works with you',
      items: [
        {
          title: 'Assess the current state',
          description: 'We review the environment, identify governance gaps, and clarify where configuration choices are creating friction or risk.',
        },
        {
          title: 'Define a practical model',
          description: 'We help shape a governance and configuration approach that matches your operational realities and quality expectations.',
        },
        {
          title: 'Build sustainable practice',
          description: 'We support the change needed for teams to adopt and maintain the model as part of normal work.',
        },
      ],
    },
    {
      title: 'From Excel register to governed SharePoint practice',
      content:
        'When a spreadsheet register is central to quality oversight, the first question is whether it remains fit for purpose. Where a SharePoint pathway is appropriate, QikSolve can help assess the current register, establish governed configuration, and prepare evidence for customer review.',
      items: [
        {
          title: 'Start with the current register',
          description: 'Assess its intended use, ownership, data, controls, and evidence needs before making a technology decision.',
        },
        {
          title: 'Verify the agreed solution',
          description: 'Use agreed acceptance criteria and repeatable checks to verify configuration and expected behaviour, with customer review and approval remaining essential.',
        },
      ],
    },
  ],
  callout: {
    title: 'Governance and configuration should make work easier to control, not harder to complete',
    content:
      'The best SharePoint model is the one that supports accountability, consistency, and evidence while still being realistic for the people who have to use it every day.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Related practice areas',
    items: [
      {
        label: 'Regulatory compliance',
        description: 'How governance, records, and accountability become workable quality practice.',
        to: '/product/regulatory-compliance',
      },
      {
        label: 'Practical GxP quality systems',
        description: 'How controlled quality systems are designed to be useful and sustainable in practice.',
        to: '/product/quality-systems',
      },
      {
        label: 'Training and enablement',
        description: 'The role-based support that helps teams adopt controls and quality expectations consistently.',
        to: '/product/training',
      },
      {
        label: 'QikSolve product pathways',
        description: 'Explore the broader service pathways that connect to this governance and quality context.',
        to: '/product',
      },
      {
        label: 'Excel register migration and validation sign-off pack',
        description: 'Assess whether a register fits a SharePoint migration with a customer-reviewable validation pack.',
        to: '/solutions/excel-register-to-sharepoint',
      },
    ],
  },
  cta: {
    title: 'Need a more workable SharePoint governance model?',
    content:
      'Bring the context, teams, and controls you need to support. We can help shape a practical approach that suits how your organisation operates.',
    label: 'Discuss your governance needs',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
